import { isMultiTenant } from "@/Configs/config";
import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { useSelectedSite } from "@/api/hooks/useSelectedSite";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";
import { type KpiNumberData, KpiNumbersView } from "@/components/v2/KpiNumbers";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import { useContext, useMemo } from "react";

import { HomePageContext } from "../../context";
import { getKpiNumbersData } from "./getKpiNumbersData";

export const KPINumbersHomePage: React.FC = () => {
  const { t } = useTrans();
  const {
    timeFilter,
    comparisonTimeFilter,
    datePreset,
    selectedTenantCategoryIds,
  } = useContext(HomePageContext);
  const { currentTerminal } = useTerminalsState();
  const { terminalConfig, wasteTypeConfig } = useConfigService();
  const { selectedSite } = useSelectedSite();

  const { data: currentData, isLoading: isLoadingCurrentData } =
    trpc.dwh.terminals.getById.useQuery(
      {
        ...(timeFilter || defaultTimeFilter),
        terminalId: currentTerminal?.id || "",
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminal",
            currentTerminal?.id,
            timeFilter?.startTimeISO,
            timeFilter?.endTimeISO,
            selectedTenantCategoryIds,
          ].toString(),
        enabled: !!currentTerminal && !!timeFilter,
      },
    );

  const { data: comparisonData, isLoading: isLoadingComparisonData } =
    trpc.dwh.terminals.getById.useQuery(
      {
        ...(comparisonTimeFilter || defaultTimeFilter),
        terminalId: currentTerminal?.id || "",
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminalComparison",
            currentTerminal?.id,
            comparisonTimeFilter?.startTimeISO,
            comparisonTimeFilter?.endTimeISO,
            selectedTenantCategoryIds,
          ].toString(),
        enabled: !!currentTerminal && !!comparisonTimeFilter,
      },
    );

  const showLoadingView = useMemo(
    () => !timeFilter || isLoadingCurrentData || isLoadingComparisonData,
    [timeFilter, isLoadingCurrentData, isLoadingComparisonData],
  );

  const hasContaminationEnabled = useMemo(
    () => !!terminalConfig?.enableContaminationRegistration,
    [terminalConfig],
  );

  const sitePath = useMemo(() => {
    let path = "";

    if (isMultiTenant && selectedSite?.siteName) {
      path = `/${selectedSite.siteName}`;
    }

    return path;
  }, [selectedSite]);

  const numbersData: KpiNumberData[] = useMemo(
    () =>
      getKpiNumbersData({
        currentData,
        comparisonData,
        datePreset,
        presetHasComparison: !!comparisonTimeFilter,
        t,
        wasteTypeConfig,
        sitePath,
        hasContaminationEnabled,
      }),
    [
      currentData,
      comparisonData,
      comparisonTimeFilter,
      datePreset,
      t,
      wasteTypeConfig,
      sitePath,
      hasContaminationEnabled,
    ],
  );

  return (
    <KpiNumbersView
      showLoadingView={showLoadingView}
      numbersData={numbersData}
      gridClassNames={`grid-cols-2 min-[621px]:grid-cols-3 ${hasContaminationEnabled ? "min-[1001px]:grid-cols-6" : "min-[1001px]:grid-cols-5"}`}
    />
  );
};
